// SettingsPlanOverviewCards.jsx

import React, { useMemo } from 'react';

// Services
import { computeEffectiveLimitsFromPlan } from 'services/bases/BaseEntitlementsService';

// CSS
import './SettingsPlanOverviewCards.css';

// Components
import Button from 'components/button/Button';

function getUsagePercent(used, limit) {
    const numericUsed = Math.max(0, Math.floor(Number(used || 0)));
    const numericLimit = Math.max(0, Math.floor(Number(limit || 0)));

    if (numericLimit <= 0) return null;

    return Math.min(100, Math.round((numericUsed / numericLimit) * 100));
}

/**
 * SettingsPlanMetric
 *
 * Renders a usage metric with an optional progress bar.
 */
function SettingsPlanMetric({
    label,
    percent,
    theme,
    value,
}) {

    // ----- RENDER ----- //

    return (
        <div className="settings-modal-plan-metric">

            {/* PLAN METRIC HEADER */}
            <div className="settings-modal-plan-metric-header">

                {/* PLAN METRIC LABEL */}
                <span
                  className="settings-modal-plan-metric-label"
                >
                    {label}
                </span>

                {/* PLAN METRIC VALUE */}
                <span
                  className="settings-modal-plan-metric-value"
                >
                    {value}
                </span>
            </div>

            {percent !== null ? (
                <div
                  className="settings-modal-plan-progress-track"
                >

                    {/* PLAN PROGRESS FILL */}
                    <div
                      className="settings-modal-plan-progress-fill"
                      style={{
                        width: `${percent}%`,
                      }}
                    />
                </div>
            ) : null}
        </div>
    );
}

/**
 * SettingsPlanOverviewCards
 *
 * Renders the current plan usage summary and optional billing details.
 *
 * @returns {ReactNode} The rendered usage summary.
 */
const SettingsPlanOverviewCards = React.memo(({
    apiUsageError,
    apiUsageLoading,
    apiUsageSummary,
    billingCancellationNotice,
    billingMonthlyTotalText,
    billingPeriodEndLabel,
    billingRecord,
    billingRenewsText,
    billingSubscriptionId,
    canCancelPaidSubscription,
    canOpenBillingPage,
    currentPlan,
    currentPlanPriceText,
    currentPlanTitle,
    isCancelSubscriptionConfirming,
    isCurrentPlanHiddenFromCatalog,
    isOpeningBillingPage,
    liveUsageError,
    liveUsageLoading,
    liveUsageSummary,
    membersUsageText,
    onOpenBillingPage,
    onStartCancelSubscription,
    shouldShowApiUsage,
    shouldShowLiveUsage,
    shouldShowRecordingUsage,
    storagePerSeatNote,
    storageReservedNote,
    storageUsageText,
    theme,
    usageAllowanceSummary,
    base,
}) => {
    // ----- MEMOS ----- //

    const billedSeatCount = useMemo(() => {
        return Math.max(1, Math.floor(Number(base?.memberCount || 1)));
    }, [base?.memberCount]);

    const effectivePlanLimits = useMemo(() => {
        return computeEffectiveLimitsFromPlan({
            billedSeatCount,
            plan: currentPlan,
        });
    }, [billedSeatCount, currentPlan]);

    const storagePercent = useMemo(() => {
        const storageBytesUsed = Math.max(0, Math.floor(Number(base?.storageBytesUsed || 0)));
        const storageBytesReserved = Math.max(0, Math.floor(Number(base?.storageBytesReserved || 0)));

        return getUsagePercent(
            storageBytesUsed + storageBytesReserved,
            effectivePlanLimits.limits.storageBytes
        );
    }, [
        effectivePlanLimits.limits.storageBytes,
        base?.storageBytesReserved,
        base?.storageBytesUsed,
    ]);

    const apiPercent = useMemo(() => {
        if (!shouldShowApiUsage || apiUsageLoading || apiUsageError) {
            return null;
        }

        return getUsagePercent(apiUsageSummary.requestCount, apiUsageSummary.limit);
    }, [
        apiUsageError,
        apiUsageLoading,
        apiUsageSummary.limit,
        apiUsageSummary.requestCount,
        shouldShowApiUsage,
    ]);

    const apiUsageText = useMemo(() => {
        if (!shouldShowApiUsage) {
            return 'Not included';
        }

        if (apiUsageLoading) {
            return 'Loading...';
        }

        if (apiUsageError) {
            return 'Unavailable';
        }

        return `${apiUsageSummary.summaryText} - ${apiUsageSummary.detailText}`;
    }, [
        apiUsageError,
        apiUsageLoading,
        apiUsageSummary.detailText,
        apiUsageSummary.summaryText,
        shouldShowApiUsage,
    ]);

    const livePercent = useMemo(() => {
        if (!shouldShowLiveUsage || liveUsageLoading || liveUsageError) {
            return null;
        }

        return getUsagePercent(
            liveUsageSummary.participantMinutesUsed,
            liveUsageSummary.participantMinutesCap
        );
    }, [
        liveUsageError,
        liveUsageLoading,
        liveUsageSummary.participantMinutesCap,
        liveUsageSummary.participantMinutesUsed,
        shouldShowLiveUsage,
    ]);

    const liveUsageText = useMemo(() => {
        if (!shouldShowLiveUsage) {
            return 'Not included';
        }

        if (liveUsageLoading) {
            return 'Loading...';
        }

        if (liveUsageError) {
            return 'Unavailable';
        }

        return liveUsageSummary.participantMinutesText;
    }, [
        liveUsageError,
        liveUsageLoading,
        liveUsageSummary.participantMinutesText,
        shouldShowLiveUsage,
    ]);

    const hasBillingDetails = Boolean(billingSubscriptionId);
    const showBillingActions = hasBillingDetails
        && !isCancelSubscriptionConfirming
        && (canOpenBillingPage || canCancelPaidSubscription);
    // ----- RENDER ----- //

    return (
        <>

            {/* PLAN USAGE CARD */}
            {usageAllowanceSummary?.shouldShow ? (
                <div
                  className="settings-modal-plan-summary-card settings-modal-plan-usage-card"
                >
                    <SettingsPlanMetric
                      label="Team usage"
                      percent={usageAllowanceSummary.percent}
                      theme={theme}
                      value={usageAllowanceSummary.text}
                    />
                </div>
            ) : null}

            {/* PLAN USAGE CARD */}
            <div
              className="settings-modal-plan-summary-card settings-modal-plan-usage-card"
            >

                <SettingsPlanMetric
                  label="Storage"
                  percent={storagePercent}
                  theme={theme}
                  value={storageUsageText}
                />

            </div>

            {/* PLAN USAGE CARD */}
            {shouldShowApiUsage && (
                <div
                  className="settings-modal-plan-summary-card settings-modal-plan-usage-card"
                >
                    <SettingsPlanMetric
                      label={`API (${apiUsageSummary.monthLabel})`}
                      percent={apiPercent}
                      theme={theme}
                      value={apiUsageText}
                    />
                </div>
            )}

            {/* PLAN USAGE CARD */}
            {shouldShowLiveUsage && (
                <div
                  className="settings-modal-plan-summary-card settings-modal-plan-usage-card"
                >
                    <SettingsPlanMetric
                      label={`Live mins (${liveUsageSummary.monthLabel})`}
                      percent={livePercent}
                      theme={theme}
                      value={liveUsageText}
                    />
                </div>
            )}

            {(hasBillingDetails || showBillingActions) && (
                <div
                  className="settings-modal-plan-summary-card settings-modal-plan-usage-card"
                >
                    {hasBillingDetails ? (
                        <div className="settings-modal-plan-usage-billing">

                            {/* PLAN USAGE BILLING TITLE */}
                            <div
                              className="settings-modal-plan-usage-billing-title"
                            >
                                Billing
                            </div>

                            <div className="settings-modal-plan-facts">
                                <div className="settings-modal-plan-fact">
                                    <span
                                      className="settings-modal-plan-fact-label"
                                      style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Billing email
                                    </span>
                                    <span
                                      className="settings-modal-plan-fact-value"
                                      style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {billingRecord?.billingEmail || '—'}
                                    </span>
                                </div>

                                <div className="settings-modal-plan-fact">
                                    <span
                                      className="settings-modal-plan-fact-label"
                                      style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Seats billed
                                    </span>
                                    <span
                                      className="settings-modal-plan-fact-value"
                                      style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {Number(billingRecord?.seatCount || 0).toLocaleString() || '0'}
                                    </span>
                                </div>

                                {billingMonthlyTotalText ? (
                                    <div className="settings-modal-plan-fact">
                                        <span
                                          className="settings-modal-plan-fact-label"
                                          style={{
                                                color: theme.foregroundMuted,
                                            }}
                                        >
                                            Monthly total
                                        </span>
                                        <span
                                          className="settings-modal-plan-fact-value"
                                          style={{
                                                color: theme.foreground,
                                            }}
                                        >
                                            {billingMonthlyTotalText}
                                        </span>
                                    </div>
                                ) : null}

                                <div className="settings-modal-plan-fact">
                                    <span
                                      className="settings-modal-plan-fact-label"
                                      style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        {billingPeriodEndLabel}
                                    </span>
                                    <span
                                      className="settings-modal-plan-fact-value"
                                      style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {billingRenewsText}
                                    </span>
                                </div>
                            </div>

                            {billingCancellationNotice ? (
                                <div
                                  className="settings-modal-note"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    {billingCancellationNotice}
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* PLAN HERO ACTIONS */}
                    {showBillingActions ? (
                        <div className="settings-modal-plan-hero-actions">
                            {canOpenBillingPage ? (
                                <Button
                                  label={isOpeningBillingPage ? 'Opening Page...' : 'Manage Billing'}
                                  enabled={!isOpeningBillingPage}
                                  onClick={onOpenBillingPage}
                                />
                            ) : null}

                            {canCancelPaidSubscription ? (
                                <Button
                                  label="Cancel Subscription"
                                  onClick={onStartCancelSubscription}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            )}
        </>
    );
});

// Dev Tools
SettingsPlanOverviewCards.displayName = 'SettingsPlanOverviewCards';

export default SettingsPlanOverviewCards;
